import { TResolution } from '@streaming/doppio-player';
import { bind } from '@streaming/mouflon-utils';

import {
  IEventDetails,
  IMMPMetricsCollected,
  TEventType,
  TMetricsCollected,
} from '@metrics/IMetrics';
import { EMetricsEvents } from '@metrics/Metrics.events';
import { RunningAverage } from '@metrics/RunningAverage';
import { RunningMax } from '@metrics/RunningMax';
import { TMMPEventBus } from '@utils/EventBus';
import { parseCSSTransformString } from '@utils/cssTransformParser';

import { MetricsCollector } from './Metrics';

const PLAYBACK_POSITION_CHECK_INTERVAL: number = 50;

export interface IDimensions {
  width: number;
  height: number;
}

export class VideoElementMetrics {
  private _collectMetricsInterval: ReturnType<typeof setInterval> | null = null;
  private _videoElement: HTMLVideoElement | null = null;
  private _isPageVisible: boolean = false;
  private _isStalledByCurrentTime: boolean = true;
  private _prevPlaybackPosition: number | null = null;
  private _checkCurrentTimeInterval: ReturnType<typeof setInterval> | null = null;
  private _isPaused: boolean = false;
  private _lastKnownVideoElementDimensions: IDimensions | null = null;
  private _videoElementResizeObserver: ResizeObserver | null = null;
  private _resolutions: TResolution[] = [];
  private _userSelectedResolution: string | null = null;
  private _userCap: IDimensions | null = null;
  private _devicePixelRatio: number = 1;

  private readonly _metricsCollector: MetricsCollector;
  private readonly _upscaleAvg: RunningAverage = new RunningAverage();
  private readonly _upscaleMax: RunningMax = new RunningMax();
  private readonly _eventBus: TMMPEventBus;
  private readonly _onSessionIdRecreatedListener: string;

  constructor(metricsCollector: MetricsCollector, eventBus: TMMPEventBus) {
    this._metricsCollector = metricsCollector;
    this._eventBus = eventBus;

    this._onSessionIdRecreatedListener = eventBus.subscribe(
      EMetricsEvents.SESSION_ID_RECREATED,
      this._onSessionIdRecreated,
    );

    this._updateDPR();
  }

  public destroy(): void {
    this._detachEventListeners();
    this._disarmMetricsCollectionInterval();
    this._disarmCurrentTimeCheck();
    this._videoElementResizeObserver?.disconnect();
    this._eventBus.unsubscribe(this._onSessionIdRecreatedListener);
  }

  public setVideoElement(videoElement: HTMLVideoElement | null): void {
    this._videoElementResizeObserver?.disconnect();
    this._detachEventListeners();
    this._disarmMetricsCollectionInterval();
    this._disarmCurrentTimeCheck();

    this._videoElement = videoElement;
    this._attachEventListeners();

    if (videoElement) {
      this._armMetricsCollectionInterval();
      this._armCurrentTimeCheck();
    }

    this._onVideoElementResized();

    if ('ResizeObserver' in globalThis && this._videoElement) {
      this._videoElementResizeObserver = new ResizeObserver(this._onVideoElementResized);
      this._videoElementResizeObserver.observe(this._videoElement);
    }
  }

  public setResolutions(resolutions: TResolution[]): void {
    this._resolutions = [...resolutions].sort((a, b) => b.height - a.height);
    this._recalculateUserCap();
  }

  public setUserSelectedResolution(userSelectedResolution: string | null): void {
    this._userSelectedResolution = userSelectedResolution;
    this._recalculateUserCap();
  }

  public setIsPaused(isPaused: boolean): void {
    this._isPaused = isPaused;
  }

  @bind
  private _updateDPR(): void {
    const mqString: string = `(resolution: ${window.devicePixelRatio}dppx)`;
    const media: MediaQueryList = matchMedia(mqString);
    media.addEventListener('change', this._updateDPR);

    this._devicePixelRatio = window.devicePixelRatio;
  }

  private _armMetricsCollectionInterval(): void {
    this._disarmMetricsCollectionInterval();

    this._collectMetricsInterval = setInterval(this._collectVideoElementMetrics, 500);
  }

  private _disarmMetricsCollectionInterval(): void {
    if (!this._collectMetricsInterval) {
      return;
    }

    clearInterval(this._collectMetricsInterval);
  }

  private _armCurrentTimeCheck(): void {
    this._disarmCurrentTimeCheck();

    this._checkCurrentTimeInterval = setInterval(
      this._checkCurrentTime,
      PLAYBACK_POSITION_CHECK_INTERVAL,
    );
  }

  private _disarmCurrentTimeCheck(): void {
    if (!this._checkCurrentTimeInterval) {
      return;
    }

    clearInterval(this._checkCurrentTimeInterval);
  }

  private _attachEventListeners(): void {
    if (!this._videoElement) {
      return;
    }

    this._videoElement.addEventListener('canplay', this._onCanPlay);
    this._videoElement.addEventListener('play', this._onPlay);
    this._videoElement.addEventListener('pause', this._onPause);
    this._videoElement.addEventListener('stalled', this._onStalledNative);
    this._videoElement.addEventListener('waiting', this._onStalledNative);
    this._videoElement.addEventListener('error', this._onErrorReported);

    document.addEventListener('visibilitychange', this._onVisibilityChanged);
    this._isPageVisible = document.visibilityState === 'visible';
  }

  private _detachEventListeners(): void {
    if (!this._videoElement) {
      return;
    }

    this._videoElement.removeEventListener('canplay', this._onCanPlay);
    this._videoElement.removeEventListener('play', this._onPlay);
    this._videoElement.removeEventListener('pause', this._onPause);
    this._videoElement.removeEventListener('stalled', this._onStalledNative);
    this._videoElement.removeEventListener('waiting', this._onStalledNative);
    this._videoElement.removeEventListener('error', this._onErrorReported);

    document.removeEventListener('visibilitychange', this._onVisibilityChanged);
  }

  private _recalculateUserCap(): void {
    if (!this._userSelectedResolution || !this._resolutions.length) {
      this._userCap = null;
      return;
    }

    const selectedResolution: TResolution | undefined = this._resolutions.find(
      ({ name }) => name === this._userSelectedResolution,
    );

    if (selectedResolution) {
      this._userCap = {
        width: selectedResolution.width,
        height: selectedResolution.height,
      };
    } else {
      this._userCap = null;
    }
  }

  @bind
  private _collectVideoElementMetrics(): void {
    if (!this._videoElement) {
      return;
    }

    this._onVideoElementResized();

    const upscaleFactor: number | null = this._calculateUpscaleFactor();

    this._eventBus.publish(EMetricsEvents.UPSCALE_CALCULATED, { upscaleFactor });

    if (upscaleFactor !== null) {
      this._upscaleAvg.addValue(upscaleFactor);
      this._upscaleMax.addValue(upscaleFactor);
    }

    const { totalVideoFrames, droppedVideoFrames } = this._videoElement.getVideoPlaybackQuality();
    let bufferedAmount: number = 0;

    try {
      Array(this._videoElement.buffered.length)
        .fill(null)
        .forEach((_, periodIndex) => {
          const [start = 0, end = 0] = [
            this._videoElement?.buffered.start(periodIndex),
            this._videoElement?.buffered.end(periodIndex),
          ];

          if (end <= (this._videoElement?.currentTime || 0)) {
            return;
          }

          bufferedAmount += end - Math.max(this._videoElement?.currentTime || 0, start);
        });
    } catch (e) {
      // Do nothing
    }

    const metrics: Partial<IMMPMetricsCollected> = {
      totalFramesDecoded: totalVideoFrames,
      totalFramesDropped: droppedVideoFrames,
      buffer: bufferedAmount,
    };

    if (this._upscaleMax.max > -Infinity) {
      metrics.upscaleFactorAvg = this._upscaleAvg.avg;
      metrics.upscaleFactorMax = this._upscaleMax.max;
    }

    this._registerMetrics(metrics);
  }

  @bind
  private _onCanPlay(): void {
    this._registerEvent('ReadyToPlay');
  }

  @bind
  private _onPlay(): void {
    if (this._videoElement && this._videoElement.readyState > 1) {
      this._registerEvent('Played');
    }
  }

  @bind
  private _onPause(): void {
    this._registerEvent('Paused');
  }

  @bind
  private _onStalledNative(): void {
    this._registerEvent('StalledNative');
  }

  @bind
  private _onVisibilityChanged(): void {
    const isVisible: boolean = document.visibilityState === 'visible';

    if (isVisible === this._isPageVisible) {
      return;
    }

    if (isVisible) {
      this._registerEvent('TabFocused');
    } else {
      if (!this._isPaused) {
        this._metricsCollector.immediatelySendMetrics();
      }
    }

    this._isPageVisible = isVisible;
  }

  @bind
  private _onErrorReported(): void {
    if (this._videoElement?.error?.code && ![1, 2].includes(this._videoElement.error.code)) {
      this._registerEvent('MediaDecodingError', {
        errorMessage: this._videoElement.error.message,
      });
    }
  }

  @bind
  private _checkCurrentTime(): void {
    if (!this._videoElement) {
      this._prevPlaybackPosition = null;
      if (!this._isStalledByCurrentTime) {
        this._isStalledByCurrentTime = true;
        this._registerEvent('Stalled');
      }
      return;
    }

    if (
      !this._prevPlaybackPosition ||
      !this._videoElement.currentTime ||
      this._videoElement.currentTime < this._prevPlaybackPosition
    ) {
      this._prevPlaybackPosition = this._videoElement.currentTime;
      return;
    }
    const isStuck: boolean = this._prevPlaybackPosition === this._videoElement.currentTime;

    this._prevPlaybackPosition = this._videoElement.currentTime;

    if (this._isStalledByCurrentTime === isStuck) {
      return;
    }

    this._isStalledByCurrentTime = isStuck;

    if (isStuck) {
      this._registerEvent('Stalled');
    } else {
      this._registerEvent('Played', { reason: 'exitFromStallByCurrentTime' });
    }
  }

  private _registerEvent(eventType: TEventType, details: IEventDetails = {}): void {
    if (!this._isPaused) {
      this._metricsCollector.registerEvent(eventType, details);
    }
  }

  private _registerMetrics(metrics: TMetricsCollected): void {
    if (!this._isPaused) {
      this._metricsCollector.registerMetrics(metrics);
    }
  }

  @bind
  private _onVideoElementResized(): void {
    if (!this._videoElement) {
      return;
    }

    if (
      this._videoElement.style.width?.includes('px') &&
      this._videoElement.style.height?.includes('px') &&
      this._videoElement.style.transform
    ) {
      const width: number = parseFloat(this._videoElement.style.width);
      const height: number = parseFloat(this._videoElement.style.height);

      const scaleFactors: { scaleX: number; scaleY: number } = parseCSSTransformString(
        this._videoElement.style.transform,
      );

      this._lastKnownVideoElementDimensions = {
        width: width * scaleFactors.scaleX,
        height: height * scaleFactors.scaleY,
      };
    } else {
      const { width, height } = this._videoElement.getBoundingClientRect();
      this._lastKnownVideoElementDimensions = {
        width,
        height,
      };
    }

    this._eventBus.publish(
      EMetricsEvents.VIDEO_ELEMENT_DIMENSIONS_CALCULATED,
      this._lastKnownVideoElementDimensions,
    );
  }

  @bind
  private _onSessionIdRecreated(): void {
    this._upscaleMax.reset();
    this._upscaleAvg.reset();
  }

  private _calculateUpscaleFactor(): number | null {
    if (
      !this._videoElement ||
      !this._lastKnownVideoElementDimensions ||
      !this._resolutions.length
    ) {
      return null;
    }

    const { videoWidth, videoHeight } = this._videoElement;

    const isLandscape: boolean = videoWidth >= videoHeight;

    const containerDimension: number = isLandscape
      ? this._lastKnownVideoElementDimensions.width
      : this._lastKnownVideoElementDimensions.height;
    const videoDimension: number = isLandscape ? videoWidth : videoHeight;

    if (!videoDimension || !containerDimension) {
      return null;
    }

    const realContainerDimension: number = containerDimension * this._devicePixelRatio;

    const userCapDimension: number = this._userCap
      ? isLandscape
        ? this._userCap.width
        : this._userCap.height
      : realContainerDimension;
    const maxQualityDimension: number = isLandscape
      ? this._resolutions[0].width
      : this._resolutions[0].height;

    const rawEstimation: number =
      Math.min(realContainerDimension, maxQualityDimension, userCapDimension) / videoDimension - 1;

    return Math.max(rawEstimation, 0);
  }
}
